import React, { useEffect, useState, useMemo } from "react";
import { Modal, Form } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import InputField from "../../../components/form/InputField";
import CommonButton from "../../../components/commonButton";
import { fetchRoles } from "../../../redux/slice/adminRoleSlice";
import { createAdminUser, updateAdminUser } from "../../../redux/slice/adminUserSlice";
import { useToast } from "../../../context/TostContext";
import withModal from "../../../components/withModal";
import CustomSelect from "../../../components/form/CustomSelect";

const AdminModalInner = ({ onClose, user, onSuccess, isView = false }) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { roles = [] } = useSelector((state) => state.adminRoles || {});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchRoles({ is_active: true }));
  }, [dispatch]);

  const roleOptions = useMemo(() => {
    return roles.map((role) => ({ value: role.id, label: role.name }));
  }, [roles]);

  const formik = useFormik({
    initialValues: {
      name: user?.name || "",
      email: user?.email || "",
      mobile: user?.mobile || "",
      role_id: user?.role_id || "",
      password: "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      mobile: Yup.string().matches(/^[0-9]{10}$/, "Mobile must be 10 digits"),
      role_id: Yup.string().required("Role is required"),
      password: Yup.string().when([], {
        is: () => !user && !isView,
        then: () => Yup.string().required("Password is required").min(6, "Min 6 characters"),
      }),
    }),
    onSubmit: async (values) => {
      if (isView) return;
      setIsLoading(true);
      try {
        let res;
        if (user) {
          const { password, ...updateData } = values;
          res = await dispatch(updateAdminUser({ id: user.id, data: updateData }));
        } else {
          res = await dispatch(createAdminUser(values));
        }

        if (res.error) {
          toast.error(res.payload?.message || "Operation failed");
        } else {
          toast.success(`User ${user ? "updated" : "created"} successfully`);
          onSuccess();
          onClose();
        }
      } catch (error) {
        toast.error("An error occurred");
      } finally {
        setIsLoading(false);
      }
    },
  });


  return (
    <Form onSubmit={formik.handleSubmit}>
      <Modal.Body className="grid gap-3">
        <InputField
          className="g-col-6"
          label={{ name: "Name" }}
          input={{
            name: "name",
            value: formik.values.name,
            onChange: formik.handleChange,
            onBlur: formik.handleBlur,
            placeholder: "Enter Name",
            disabled: isView,
          }}
          validation={{
            isInvalid: formik.touched.name && !!formik.errors.name,
            message: formik.errors.name,
          }}
        />
        <InputField
          className="g-col-6"
          label={{ name: "Email" }}
          input={{
            name: "email",
            type: "email",
            value: formik.values.email,
            onChange: formik.handleChange,
            onBlur: formik.handleBlur,
            placeholder: "Enter Email",
            disabled: !!user || isView,
          }}
          validation={{
            isInvalid: formik.touched.email && !!formik.errors.email,
            message: formik.errors.email,
          }}
        />
        <InputField
          className="g-col-6"
          label={{ name: "Mobile" }}
          input={{
            name: "mobile",
            value: formik.values.mobile,
            onChange: formik.handleChange,
            onBlur: formik.handleBlur,
            placeholder: "Enter Mobile",
            disabled: isView,
          }}
          validation={{
            isInvalid: formik.touched.mobile && !!formik.errors.mobile,
            message: formik.errors.mobile,
          }}
        />

        <CustomSelect
          className="g-col-6"
          label={{ name: "Role" }}
          options={roleOptions}
          value={roleOptions.find((opt) => opt.value === formik.values.role_id)}
          onChange={(option) => !isView && formik.setFieldValue("role_id", option?.value || "")}
          onBlur={formik.handleBlur}
          placeholder="Select Role"
          isClearable={!isView}

          isDisabled={isView}
          validation={{
            isInvalid: formik.touched.role_id && !!formik.errors.role_id,
            message: formik.errors.role_id,
          }}
        />

        {!user && !isView && (
          <InputField
            className="g-col-12"
            label={{ name: "Password" }}
            input={{
              name: "password",
              type: "password",
              value: formik.values.password,
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
              placeholder: "Enter Password",
            }}
            validation={{
              isInvalid: formik.touched.password && !!formik.errors.password,
              message: formik.errors.password,
            }}
          />
        )}
      </Modal.Body>
      {!isView && (
        <Modal.Footer>
          <CommonButton
            title={isLoading ? "Saving..." : user ? "Update" : "Add"}
            variant="primary"
            type="submit"
            disabled={isLoading}
          />
        </Modal.Footer>
      )}
    </Form>
  );
};

const AdminModal = withModal(AdminModalInner);

export default AdminModal;
